// React
import React, { useState } from 'react'

// Components
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

// External
import {
  Info,
  Paintbrush,
  Waves,
  Box,
  Image as ImageIcon,
  ScanFaceIcon,
  DropletIcon,
  BlocksIcon,
  SparklesIcon,
} from 'lucide-react'
import { useTranslation } from 'next-i18next'

// TODO: localize
const STYLE_OPTIONS = {
  DEFAULT: {
    title: 'Original image',
    directory: 'default',
    icon: <ImageIcon className='mr-4 h-4 w-4' />,
  },
  GHIBLI: {
    title: 'Studio Ghibli animation style',
    directory: 'studio-ghibli-animation-style',
    icon: <ScanFaceIcon className='mr-4 h-4 w-4' />,
  },
  PICASSO: {
    title: 'Picasso-style cubism',
    directory: 'picasso-style-cubism',
    icon: <Box className='mr-4 h-4 w-4' />,
  },
  PIXEL_ART: {
    title: 'Pixel art style',
    directory: 'pixel-art-style',
    icon: <BlocksIcon className='mr-4 h-4 w-4' />,
  },
  PENCIL_SKETCH: {
    title: 'Pencil sketch',
    directory: 'pencil-sketch',
    icon: <Paintbrush className='mr-4 h-4 w-4' />,
  },
  VAPORWAVE: {
    title: 'Vaporwave aesthetic',
    directory: 'vaporwave-aesthetic',
    icon: <Waves className='mr-4 h-4 w-4' />,
  },
  WATERCOLOR: {
    title: 'Watercolor painting',
    directory: 'watercolor-painting',
    icon: <DropletIcon className='mr-4 h-4 w-4' />,
  },
}

type StyleImageButtonProps = {
  isLoading?: boolean
  onClick: (style: string) => void
}

const StyleImageButton: React.FC<StyleImageButtonProps> = ({
  isLoading = false,
  onClick,
}) => {
  const { t } = useTranslation('components/meme-editor')
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                size='sm'
                variant='outline'
                className='w-full bg-gradient-to-r from-indigo-200 via-purple-200 to-blue-200 font-semibold'
                disabled={isLoading}
                onClick={() => setIsOpen(!isOpen)}
              >
                <SparklesIcon className='mr-2 h-4 w-4 text-yellow-600' />
                {t('buttons.styleImage.label')}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p className='w-64'>
              <Info className='mb-1 mr-1 inline h-3 w-3' />
              {t('buttons.styleImage.tooltip')}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <PopoverContent className='w-72'>
        <div className='grid gap-2'>
          <label className='text-sm font-bold'>
            {t('buttons.styleImage.dropdownLabel')}
          </label>
          <div className='border-b border-gray-200' />

          {Object.entries(STYLE_OPTIONS).map(([key, styleValue]) => (
            <Button
              key={key}
              size='sm'
              variant='ghost'
              className='dark:hover:bg-slate-800 justify-start hover:bg-gray-100'
              onClick={() => {
                onClick(styleValue.directory)
                setIsOpen(false)
              }}
            >
              {styleValue.icon}
              <span>{styleValue.title}</span>
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { StyleImageButton }
